import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { FileText, Trash, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

export default function AppointmentFilesDialog({ appointment, open, onClose, canEdit }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [category, setCategory] = useState("paperwork");
  const fileInputRef = useRef(null);

  const load = async () => {
    if (!appointment) return;
    const { data } = await api.get(`/appointments/${appointment.id}/files`);
    setFiles(data);
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, appointment?.id]);

  const onPick = () => fileInputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/appointments/${appointment.id}/files?category=${encodeURIComponent(category)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${f.name}`);
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (file) => {
    if (!window.confirm(`Delete ${file.original_filename}?`)) return;
    await api.delete(`/files/${file.id}`);
    toast.success("Removed");
    load();
  };

  const download = async (file) => {
    setDownloadingId(file.id);
    try {
      const { data } = await api.post(`/files/${file.id}/signed-url`);
      // Open the short-lived signed URL in a new tab (backend is at API origin)
      window.open(`${API}${data.url.replace(/^\/api/, "")}`, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate download link");
    } finally {
      setDownloadingId(null);
    }
  };

  const catBadge = (c) => c === "commission_schedule"
    ? { label: "Commission Schedule", color: "#34d399" }
    : { label: "Paperwork", color: "#2563eb" };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-2xl" data-testid="files-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <FileText weight="bold" /> Files — {appointment?.agent_name} × {appointment?.carrier_name}
          </DialogTitle>
        </DialogHeader>

        {canEdit && (
          <div className="brut-sm p-3 bg-[#f4f4f5] flex flex-wrap items-end gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-1">Category</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 border-2 border-black px-3 bg-white font-medium text-sm" data-testid="upload-category">
                <option value="paperwork">Paperwork</option>
                <option value="commission_schedule">Commission Schedule</option>
              </select>
            </div>
            <Button
              onClick={onPick}
              disabled={uploading}
              className="rounded-none bg-[#2563eb] text-white border-2 border-black hover:bg-[#1d4ed8] shadow-[3px_3px_0_0_#09090b] active:shadow-none active:translate-x-1 active:translate-y-1 font-bold uppercase tracking-widest text-xs h-10"
              data-testid="upload-btn"
            >
              <UploadSimple size={14} weight="bold" className="mr-1" />
              {uploading ? "UPLOADING…" : "UPLOAD"}
            </Button>
            <input ref={fileInputRef} type="file" hidden onChange={onFile}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.xlsx,.xls,.csv,.txt" data-testid="file-input" />
            <div className="text-xs text-zinc-500">Max 15MB · PDF, images, docs</div>
          </div>
        )}

        <div className="space-y-2 mt-3 max-h-[50vh] overflow-y-auto" data-testid="files-list">
          {files.length === 0 && (
            <div className="text-center text-zinc-500 py-8 border-2 border-dashed border-zinc-300">
              No files yet. {canEdit && "Upload commission schedules or signed paperwork above."}
            </div>
          )}
          {files.map((f) => {
            const cat = catBadge(f.category);
            return (
              <div key={f.id} className="flex items-center justify-between border-2 border-black px-3 py-2 bg-white gap-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 border-2 border-black flex items-center justify-center bg-[#f4f4f5]">
                    <FileText size={18} weight="bold" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate text-sm">{f.original_filename}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="inline-block border-2 border-black px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" style={{ background: cat.color }}>
                        {cat.label}
                      </span>
                      <span className="text-[11px] text-zinc-500">
                        {(f.size / 1024).toFixed(0)} KB · {f.created_at.slice(0, 10)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => download(f)} disabled={downloadingId === f.id}
                    className="border-2 border-black p-2 hover:bg-[#34d399] disabled:opacity-50" data-testid={`download-file-${f.id}`}>
                    <DownloadSimple size={14} weight="bold" />
                  </button>
                  {canEdit && (
                    <button onClick={() => del(f)} className="border-2 border-black p-2 hover:bg-[#fb7185]" data-testid={`delete-file-${f.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
